import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@nexus/ui";
import { AdminBuildIdentity } from "@/features/admin/components/AdminBuildIdentity";
import { useChangelogPermissions } from "@/features/changelog/hooks/useChangelogPermissions";
import { useIsSuperTenant } from "@/features/auth/hooks/usePermissions";

export type AdminTab =
  | "events"
  | "deploys"
  | "roundups"
  | "scheduledJobs"
  | "tokenUsage"
  | "bigQuery"
  | "settings";

interface AdminTabStripProps {
  activeTab: AdminTab;
  onTabChange: (tab: AdminTab) => void;
}

/** Shared between the tab and its panel so each can point at the other. */
export function adminTabId(tab: AdminTab): string {
  return `admin-tab-${tab}`;
}

export function adminTabPanelId(tab: AdminTab): string {
  return `admin-panel-${tab}`;
}

/**
 * The panel's tab chrome. Live-build identity sits at the trailing edge as an
 * environment chip; the rest of the record opens on click so seven tabs never
 * truncate a branch name.
 */
export function AdminTabStrip({ activeTab, onTabChange }: AdminTabStripProps) {
  const { t } = useTranslation();
  const {
    canList: canSeeDeploys,
    canListDigests: canSeeRoundups,
    canViewSettings: canSeeSettings,
  } = useChangelogPermissions();
  // Schedules are platform infra with no tenant dimension — same trust level
  // as the "Publish event" action, so the tab follows the same gate.
  const isSuperTenant = useIsSuperTenant();
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Deploys and roundups are peers of Events rather than a second tab strip
  // inside a "Changelog" tab: they are different audiences and different work,
  // and nesting tabs inside tabs only hid that.
  const tabs: Array<{ id: AdminTab; label: string }> = [
    { id: "events", label: t("admin.header.tabs.events", "Events") },
    ...(canSeeDeploys
      ? [{ id: "deploys" as const, label: t("admin.header.tabs.deploys", "Releases") }]
      : []),
    ...(canSeeRoundups
      ? [{ id: "roundups" as const, label: t("admin.header.tabs.roundups", "Roundups") }]
      : []),
    ...(isSuperTenant
      ? [
          {
            id: "scheduledJobs" as const,
            label: t("admin.header.tabs.scheduledJobs", "Scheduled Jobs"),
          },
          // Token spend is cross-tenant, so it follows the same gate as
          // schedules rather than a grantable permission.
          {
            id: "tokenUsage" as const,
            label: t("admin.header.tabs.tokenUsage", "Token Usage"),
          },
          // Warehouse spend is cross-tenant for the same reason token spend is:
          // one org reading another org's query volume is a leak no org should
          // be able to grant, so there is no permission to grant in its place.
          {
            id: "bigQuery" as const,
            label: t("admin.header.tabs.bigQuery", "BigQuery Usage"),
          },
        ]
      : []),
    // Last, and after the lists it governs: it is where you go once, not the
    // tab you work in.
    ...(canSeeSettings
      ? [{ id: "settings" as const, label: t("admin.header.tabs.settings", "Settings") }]
      : []),
  ];

  /** Arrow keys move between tabs, which a declared tablist is expected to do. */
  function handleTabKeyDown(event: React.KeyboardEvent, index: number) {
    const lastIndex = tabs.length - 1;
    const nextIndex =
      event.key === "ArrowRight"
        ? (index + 1) % tabs.length
        : event.key === "ArrowLeft"
          ? (index + lastIndex) % tabs.length
          : event.key === "Home"
            ? 0
            : event.key === "End"
              ? lastIndex
              : null;

    const nextTab = nextIndex === null ? undefined : tabs[nextIndex];
    if (nextIndex === null || !nextTab) return;
    event.preventDefault();
    onTabChange(nextTab.id);
    tabRefs.current[nextIndex]?.focus();
  }

  return (
    // One band, not two. The nav above already says which section this is, so a
    // second row repeating "Admin" spent 48px to tell nobody anything. Live-build
    // identity is an environment chip that opens a popover — the branch and
    // releaser are too long to live in this row next to seven tabs.
    <header className="flex shrink-0 items-stretch gap-3 overflow-hidden border-b border-border bg-background px-6 sm:gap-4">
      <h1 className="sr-only">{t("admin.header.title", "Admin")}</h1>

      <div
        role="tablist"
        data-testid="admin-header-tabs"
        className={cn(
          "flex min-w-0 flex-1 items-stretch gap-1 overflow-x-auto overflow-y-hidden overscroll-x-contain",
          // Scroll when tabs overflow, but keep the chrome clean — a thin track
          // next to the env badge reads as a layout bug, not an affordance.
          "[scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden",
        )}
      >
        {tabs.map((tab, index) => {
          const isActive = tab.id === activeTab;
          return (
            <button
              key={tab.id}
              ref={(node) => {
                tabRefs.current[index] = node;
              }}
              type="button"
              role="tab"
              id={adminTabId(tab.id)}
              aria-selected={isActive}
              // Only the selected panel is mounted, so pointing an inactive tab
              // at it would be a dangling IDREF.
              aria-controls={isActive ? adminTabPanelId(tab.id) : undefined}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onTabChange(tab.id)}
              onKeyDown={(event) => handleTabKeyDown(event, index)}
              className={cn(
                "-mb-px inline-flex shrink-0 items-center border-b-2 px-3 py-3 text-xs transition-colors",
                "min-h-11 [@media(hover:hover)_and_(pointer:fine)]:min-h-0",
                isActive
                  ? "border-primary font-semibold text-foreground"
                  : "border-transparent font-normal text-muted-foreground hover:text-foreground",
              )}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <AdminBuildIdentity
        onOpenReleases={
          canSeeDeploys && activeTab !== "deploys" ? () => onTabChange("deploys") : undefined
        }
      />
    </header>
  );
}
